import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Search as SearchIcon, Check, Users } from "lucide-react";
import { VideoCard } from "../components/VideoCard";
import { toast } from "sonner";

const FILTERS = [
  { k: "all", label: "All" },
  { k: "users", label: "Users" },
  { k: "groups", label: "Groups" },
  { k: "debates", label: "Debates" },
  { k: "thoughts", label: "Thoughts" },
];

export default function Search() {
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const search = async () => {
    if (!q.trim()) return;
    setLoading(true);
    try {
      const r = await api.get(`/search?q=${encodeURIComponent(q)}&filter=${filter}`);
      setResults(r.data);
    } finally { setLoading(false); }
  };

  const follow = async (id) => {
    try {
      await api.post(`/users/${id}/follow`);
      toast.success("Following");
      // Update the user's is_following in place instead of removing them from results
      setResults((prev) => prev ? {
        ...prev,
        users: prev.users.map((u) => u.id === id ? { ...u, is_following: true, followers_count: (u.followers_count || 0) + 1 } : u),
      } : prev);
    } catch { toast.error("Failed"); }
  };
  const joinGroup = async (id) => {
    try {
      await api.post(`/groups/${id}/join`);
      toast.success("Joined");
      setResults((prev) => prev ? { ...prev, groups: prev.groups.filter((g) => g.id !== id) } : prev);
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 lg:px-8 py-6 lg:py-10">
      <div className="border-b border-border pb-6 mb-8">
        <div className="text-xs font-semibold text-muted-foreground tracking-wider mb-1">Discover</div>
        <h1 className="font-serif text-4xl mb-6">Global search</h1>
        <div className="flex gap-2">
          <Input
            className="rounded-xl h-12 bg-background border-border text-lg"
            placeholder="Search users, groups, debates…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && search()}
            data-testid="search-input"
          />
          <Button onClick={search} className="rounded-xl h-12 px-6 text-xs font-semibold tracking-wider bg-[hsl(var(--primary))] text-white" data-testid="search-btn">
            <SearchIcon className="w-4 h-4 mr-2" /> Search
          </Button>
        </div>
        <div className="flex gap-2 mt-4 flex-wrap">
          {FILTERS.map((f) => (
            <button
              key={f.k}
              onClick={() => setFilter(f.k)}
              data-testid={`search-filter-${f.k}`}
              className={`text-xs font-semibold tracking-wider px-4 py-2 border transition-colors ${filter === f.k ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))] text-white" : "border-border text-muted-foreground hover:bg-secondary"}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="text-xs font-semibold text-muted-foreground tracking-wider">Searching…</div>}

      {results && (
        <div className="space-y-10">
          {results.users?.length > 0 && (
            <section>
              <div className="text-xs font-semibold text-muted-foreground tracking-wider mb-4">Users ({results.users.length})</div>
              <div className="grid gap-2">
                {results.users.map((u) => (
                  <div key={u.id} className="border border-border p-4 flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-secondary border border-border flex items-center justify-center font-mono">
                      {u.username?.[0]?.toUpperCase()}
                    </div>
                    <Link to={`/profile/${u.username}`} className="flex-1 hover:underline">
                      <div className="text-sm flex items-center gap-1">
                        @{u.username}
                        {u.verification_status === "verified" && <Check className="w-3 h-3 text-[hsl(var(--support))]" />}
                      </div>
                      <div className="text-xs font-semibold text-muted-foreground tracking-wider">{u.full_name} · {u.followers_count} followers</div>
                    </Link>
                    <Button
                      variant={u.is_following ? "outline" : "default"}
                      size="sm"
                      className={`rounded-full ${u.is_following ? "" : "bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90"}`}
                      onClick={() => !u.is_following && follow(u.id)}
                      disabled={u.is_following}
                      data-testid={`search-follow-${u.username}`}
                    >{u.is_following ? "Following" : "Follow"}</Button>
                  </div>
                ))}
              </div>
            </section>
          )}

          {results.groups?.length > 0 && (
            <section>
              <div className="text-xs font-semibold text-muted-foreground tracking-wider mb-4">Groups ({results.groups.length})</div>
              <div className="grid gap-2">
                {results.groups.map((g) => (
                  <div key={g.id} className="border border-border p-4 flex items-center gap-4">
                    <div className="w-10 h-10 bg-[hsl(var(--support))]/10 border border-[hsl(var(--support))]/30 flex items-center justify-center">
                      <Users className="w-4 h-4 text-[hsl(var(--support))]" />
                    </div>
                    <Link to={`/groups/${g.id}`} className="flex-1 hover:underline">
                      <div className="text-sm">{g.name}</div>
                      <div className="text-xs font-semibold text-muted-foreground tracking-wider">{g.members?.length || 0} members</div>
                    </Link>
                    {!g.is_private && (
                      <Button variant="outline" size="sm" className="rounded-xl text-xs font-semibold tracking-wider" onClick={() => joinGroup(g.id)}>Join</Button>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {results.debates?.length > 0 && (
            <section>
              <div className="text-xs font-semibold text-muted-foreground tracking-wider mb-4">Debates ({results.debates.length})</div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {results.debates.map((d) => <VideoCard key={d.id} item={d} type="debate" />)}
              </div>
            </section>
          )}

          {results.thoughts?.length > 0 && (
            <section>
              <div className="text-xs font-semibold text-muted-foreground tracking-wider mb-4">Thoughts ({results.thoughts.length})</div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {results.thoughts.map((t) => <VideoCard key={t.id} item={t} type="thought" />)}
              </div>
            </section>
          )}

          {results.users?.length === 0 && results.groups?.length === 0 && results.debates?.length === 0 && results.thoughts?.length === 0 && (
            <div className="border border-dashed border-border p-16 text-center text-xs font-semibold text-muted-foreground tracking-wider">
              No results for &ldquo;{q}&rdquo;
            </div>
          )}
        </div>
      )}
    </div>
  );
}
